import { z } from 'zod'
import { garanLabelDataSchema, manufacturerGuaranteeSnapshotSchema } from '../manufacturerGuarantees'
import typeguard from '../typeguard'

import { imagesSchema, imageSrcSchema, imageSrcsSchema } from './images'
import { discounTypeSchema } from '../discounTypes'
import { crosssellTypesSchema } from '../crosssellTypes'
import { productPurchaseModeSchema } from '../productPurchaseMode'
import { countdownVariantSchema, quantityDiscountSchema } from '../discountShop'

// --- Neue Definition für "alsoPurchased" ---
const produktPurchasedItemSchema = z.object({
  product: z.number(),
  count: z.number(),
})
export const alsoPurchasedSchema = z.array(produktPurchasedItemSchema)
export type AlsoPurchased = z.infer<typeof alsoPurchasedSchema>
// --- Ende neue Definition ---

const breadcrumbItemSchema = z.object({
  title: z.string(),
  href: z.string().optional(),
})
const breadcrumbSchema = z.array(breadcrumbItemSchema)
export type BreadcrumbItem = z.infer<typeof breadcrumbItemSchema>

const optionSchema = z.object({
  id: z.number(),
  name: z.string(),
  images: imagesSchema.optional(),
  uncultivable: z.boolean().optional(),
})
export type option = z.infer<typeof optionSchema>

const optionsSchema = z.array(optionSchema)
export type options = z.infer<typeof optionsSchema>

const attributeSchema = z.object({
  id: z.number(),
  name: z.string(),
  options: optionsSchema,
})
export type attribute = z.infer<typeof attributeSchema>

const attributesSchema = z.array(attributeSchema)
export type attributes = z.infer<typeof attributesSchema>

export type discounType = z.infer<typeof discounTypeSchema>

export const discountSchema = z.object({
  id: z.number(),
  metaTitle: z.string(),
  type: discounTypeSchema,
  countdownVariant: countdownVariantSchema.optional(),
  dateEnd: z.string().optional(),
  price: z.number(),
  priceAfterStock: z.number().optional(),
  stockDiscountLimit: z.number().optional(),
  limitStockDiscountToStock: z.boolean().optional(),
  quantityDiscount: quantityDiscountSchema.optional(),
  showPercent: z.boolean().optional(),
  shops: z.array(z.number()).optional(),
})
export type discount = z.infer<typeof discountSchema>

export const discountPriceSchema = z.object({
  regularPrice: z.number(),
  discountPrice: z.number(),
  discountPriceAfterStock: z.number().optional(),
  stockDiscountLimit: z.number().optional(),
  limitStockDiscountToStock: z.boolean().optional(),
  quantityDiscount: quantityDiscountSchema.optional(),
  title: z.string(),
  type: discounTypeSchema,
  countdownVariant: countdownVariantSchema.optional(),
  dateEnd: z.string().optional(),
  showPercent: z.boolean().optional(),
})
export type discountPrice = z.infer<typeof discountPriceSchema>

export const cartPriceLockSchema = z.object({
  price: z.union([
    z.number(),
    discountPriceSchema,
  ]),
  createdAt: z.string(),
  expiresAt: z.string(),
  hash: z.string(),
})

export const productCouponPromotionSchema = z.object({
  couponId: z.number(),
  code: z.string(),
  title: z.string().optional(),
  text: z.string().optional(),
  badgeText: z.string().optional(),
  expirationDate: z.string().optional(),
  color: z.enum(['green', 'red', 'black']).optional(),
  showOnListing: z.boolean().optional(),
  showOnProduct: z.boolean().optional(),
})
export type tProductCouponPromotion = z.infer<typeof productCouponPromotionSchema>

const combinationSchema = z.object({
  key: z.string(),
  attributes: z.array(z.string()),
  options: z.array(z.number()),
  ean: z.string().optional(),
  sku: z.string().optional(),
  mpn: z.string().optional(),
  originCountry: z.string().optional(),
  weight: z.number().optional(),
  price: z.number(),
  deliverytime: z.number(),
  stockDeliverytime: z.number().optional(),
  images: imagesSchema.optional(),
  discount: discountSchema.optional(),
  priceLock: cartPriceLockSchema.optional(),
  stock: z.number().optional(),
  cultivable: z.boolean().optional(),
  hash: z.string(),
})
export type combination = z.infer<typeof combinationSchema>

const combinationsSchema = z.array(combinationSchema)
export type combinations = z.infer<typeof combinationsSchema>

const crossSellMetaSchema = z.object({
  hersteller: z.string().optional(),
  serie: z.string().optional(),
  farbe: z.string().optional(),
  teil: z.string().optional(),
})
export type tCrossSellMeta = z.infer<typeof crossSellMetaSchema>

export const crossSellSectionSchema = z.object({
  art: crosssellTypesSchema,
  titel: z.string().optional(),
  products: z.lazy(() => productsPreviewSchema),
})
export type tCrossSellSection = z.infer<typeof crossSellSectionSchema>
export const crossSellSectionsSchema = z.array(crossSellSectionSchema)
export type tCrossSellSections = z.infer<typeof crossSellSectionsSchema>
export const isCrossSellSections = typeguard<tCrossSellSections>(crossSellSectionsSchema)

export const productMagazineReferenceSchema = z.object({
  id: z.number(),
  title: z.string(),
  slug: z.string(),
  href: z.string().optional(),
  mode: z.enum(['magazine', 'productImages', 'faq']).default('magazine'),
  tags: z.array(z.string()).default([]),
  date: z.string().optional(),
  issue: z.string().optional(),
  subtitle: z.string().optional(),
  coverImage: z.object({
    src: z.string().optional(),
    alt: z.string().optional(),
    caption: z.string().optional(),
    aiGenerated: z.boolean().optional(),
  }).optional(),
  pagesCount: z.number(),
  pageIndex: z.number().optional(),
  faqItems: z.array(z.object({
    question: z.string(),
    answer: z.string(),
    topic: z.string().optional(),
  })).default([]),
})
export type tProductMagazineReference = z.infer<typeof productMagazineReferenceSchema>

const productSchema = z.object({
  id: z.number(),
  name: z.string(),
  path: z.string(),
  metaTitle: z.string(),
  categorie: z.number().optional(),
  brand: z.number(),
  brandTitel: z.string(),
  brandPath: z.string().optional(),
  brandLogo: z.string().optional(),
  brandMetaTitel: z.string(),
  brandDescription: z.string(),
  manufacturerAddress: z.string().optional(),
  manufacturerGuarantees: z.array(manufacturerGuaranteeSnapshotSchema).optional(),
  manufacturerGuaranteesUnavailable: z.boolean().optional(),
  manufacturerGuaranteeLabels: z.record(z.array(z.object({
    programId: z.string(),
    label: garanLabelDataSchema,
    revision: z.string(),
  }))).optional(),

  description: z.string(),
  descriptionDetails: z.string(),
  descriptionShort: z.string(),
  hook: z.string().optional(),
  productType: z.string().optional(),
  listingGroupKey: z.string().optional(),
  technicalData: z.array(z.string()).optional(),
  keywords: z.array(z.string()).optional(),
  images: imagesSchema,
  shippingType: z.number(),
  attributes: attributesSchema.optional(),
  combinations: combinationsSchema,
  onlyStock: z.literal(true).optional(),
  purchaseMode: productPurchaseModeSchema.optional(),
  inquireOnly: z.literal(true).optional(),
  crossSellTechnicalTags: z.array(z.string()).optional(),
  breadcrumb: breadcrumbSchema.optional(),

  alsoPurchased: alsoPurchasedSchema.optional(),
  crossSellSections: crossSellSectionsSchema.optional(),
  magazines: z.array(productMagazineReferenceSchema).optional(),
  couponPromotion: productCouponPromotionSchema.optional(),
})
export type tProduct = z.infer<typeof productSchema>
export const isProduct = typeguard<tProduct>(productSchema)

const listingGroupItemSchema = z.object({
  id: z.number(),
  key: z.string(),
  name: z.string().optional(),
  metaTitle: z.string().optional(),
  label: z.string().optional(),
  color: z.string().optional(),
  size: z.string().optional(),
  url: z.string().optional(),
  image: imageSrcSchema.optional(),
  price: z.number().optional(),
  deliverytime: z.number().optional(),
  discount: discountSchema.optional(),
  stock: z.number().optional(),
  onlyStock: z.literal(true).optional(),
  couponPromotion: productCouponPromotionSchema.optional(),
})
export type tListingGroupItem = z.infer<typeof listingGroupItemSchema>

const roductPreviewSchema = z.object({
  id: z.number(),
  categorie: z.number().optional(),
  key: z.string(),
  hash: z.string().optional(),
  name: z.string(),
  price: z.number(),
  ean: z.string().optional(),
  sku: z.string().optional(),
  mpn: z.string().optional(),
  originCountry: z.string().optional(),
  weight: z.number().optional(),
  deliverytime: z.number(),
  stockDeliverytime: z.number().optional(),
  stock: z.number().optional(),
  onlyStock: z.literal(true).optional(),
  purchaseMode: productPurchaseModeSchema.optional(),
  inquireOnly: z.literal(true).optional(),
  path: z.string(),
  metaTitle: z.string(),
  brand: z.number(),
  brandTitel: z.string(),
  brandMetaTitel: z.string(),
  hook: z.string().optional(),
  images: imageSrcsSchema,
  discount: discountSchema.optional(),
  priceLock: cartPriceLockSchema.optional(),
  shippingType: z.number(),
  options: z.array(z.number()).optional(),
  attributes: z.array(z.string()),
  listingVariantLabel: z.string().optional(),
  crossSellMeta: crossSellMetaSchema.optional(),
  listingGroupKey: z.string().optional(),
  listingGroupItems: z.array(listingGroupItemSchema).optional(),
  listingGroupCount: z.number().optional(),
  couponPromotion: productCouponPromotionSchema.optional(),
})
export type tProductPreview = z.infer<typeof roductPreviewSchema>
export const isProductPreview = typeguard<tProductPreview>(roductPreviewSchema)

export const productsPreviewSchema = z.array(roductPreviewSchema)
export type tProductsPreview = z.infer<typeof productsPreviewSchema>
export const isProductsPreview = typeguard<tProductsPreview>(productsPreviewSchema)
